import { Button, Spin } from "antd";
import { useContext, useMemo, useState } from "react";
import { Check, Clipboard, Download } from "react-feather";
import { useTranslation } from "react-i18next";
import { TImage } from "../../types/album.type";
import { copyToClipboard } from "../../utils/clipboard";
import { downloadTxtFile } from "../../utils/common";
import { useRootSelector } from "../../stores/reducers/root";
import { AbilityContext, PERMISSION } from "../../services/ability.service";

type SummaryTxtProps = {
  images: TImage[];
  name: string;
  isLoading: boolean;
  hasSummaryData: boolean;
};

const removeExtension = (fileName: string) =>
  fileName.replace(/\.[^/.]+$/, "");

const SummaryTxt = ({
  images,
  name,
  isLoading,
  hasSummaryData,
}: SummaryTxtProps) => {
  const { t } = useTranslation();
  const [actionStatus, setActionStatus] = useState<"copy" | "download" | null>(
    null
  );

  const user = useRootSelector((state) => state.user.userProfile);
  const ability = useContext(AbilityContext);

  const domain = ability.can("can", PERMISSION.PRIVATE_DOMAIN)
  ? (user.setting?.domain_info?.status === "active"
      ? user.setting?.domain
      : null) ??
    user.setting?.sub_domain_name ??
    window.location.host
  : window.location.host;

  const { summaryContent, hasEntries, jsonContent } = useMemo(() => {
    const sanitizedImages = images ?? [];
    
    // Tạo mảng JSON từ tất cả ảnh có ít nhất một trong: like, comment, hoặc tag
    const jsonArray = sanitizedImages
      .filter((image) => {
        const hasLike = image.is_liked === 1;
        const hasComment = image.comments?.some((item) => item.comment?.trim());
        const hasTag = image.tags?.length > 0;
        return hasLike || hasComment || hasTag;
      })
      .map((image) => {
        const id = removeExtension(image.file_name);
        const star = image.is_liked === 1 ? 5 : null;
        
        const commentObj = image.comments?.find((item) => item.comment?.trim());
        const comment = commentObj?.comment?.trim() || null;
        
        const tag = image.tags?.length > 0 
          ? image.tags.map((tag) => tag.name).join(",")
          : null;
        
        return {
          id,
          star,
          comment,
          tag,
          copyright: user.name || "",
          copyright_url: `https://${domain}`,
        };
      });

    const hasEntries = jsonArray.length > 0;
    
    // Format JSON với indentation để hiển thị đẹp
    const formattedJson = JSON.stringify(jsonArray, null, 2);
    
    // Tạo HTML để hiển thị JSON với syntax highlighting cơ bản
    const content = `<pre class="text-sm">${formattedJson.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre>`;

    return {
      summaryContent: content,
      hasEntries,
      jsonContent: formattedJson, // Lưu JSON string để copy/download
    };
  }, [images, user, domain]);

  const disabledActions =
    isLoading || !hasSummaryData || !hasEntries || !summaryContent;

  return (
    <div className="flex flex-col gap-2">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spin />
        </div>
      ) : (
        <>
          <span>{t("dashboard.albums.action.download.txt.contentAll")}</span>
          <div className="bg-stone-200 text-gray-500 rounded-lg py-2 px-3 whitespace-pre overflow-auto min-h-48 max-h-96 cursor-default" dangerouslySetInnerHTML={{ __html: summaryContent }} />
          <div className="w-full grid grid-cols-2 gap-4">
            <Button
              type="default"
              className="my-4 transition-all success"
              disabled={disabledActions}
              onClick={async () => {
                const success = await copyToClipboard(jsonContent || "");
                if (success) {
                  setActionStatus("copy");
                  setTimeout(() => setActionStatus(null), 1000);
                }
              }}
            >
              {actionStatus === "copy" ? (
                <Check className="w-4" />
              ) : (
                <Clipboard className="w-4" />
              )}
              {t("dashboard.albums.action.download.modalShare.btnCopy")}
            </Button>
            <Button
              type="default"
              className={`my-4 transition-all success ${
                actionStatus === "download" ? "opacity-70" : ""
              }`}
              disabled={disabledActions}
              loading={actionStatus === "download"}
              onClick={() => {
                setActionStatus("download");
                downloadTxtFile(jsonContent || "", name, "shotpik");
                setTimeout(() => setActionStatus(null), 500);
              }}
            >
              {actionStatus !== "download" && <Download className="w-4" />}
              {t("dashboard.albums.action.download.photos.btn")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default SummaryTxt;
